import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { BuscaRemota, CampoControlado, CampoData, Entrada, EntradaComMascara, EntradaSensivel, mascaraCep, Rotulo, Selecao, valorExtra, type DefCampo, type Opcao } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, ErroRequisicao, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { celulaCsv } from '../../nucleo/csv';
import { DialogoCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import { EXTRAS_FORA_DO_CRM } from '../../nucleo/extras-crm';
import { EdicaoRapida } from '../../componentes/edicao-rapida';
import { iniciais } from '../../nucleo/discreto';
import { mascaraTelefoneDigitando, nomePessoa } from '../../nucleo/formato';
import { EXEMPLOS_MASCARA } from '../../nucleo/mascaras';
import { useSessao } from '../../nucleo/sessao';
import { PRESETS_DATA } from './crm-filtros';
import { consultar, enderecoDaConsulta, estadoDaResposta, type EstadoConsulta } from './consulta';
import type { Endereco, Parceiro } from './tipos';
import { FalhaNoBloco, porBlocos, TETO_ACAO_EM_MASSA } from '@malgoos/shared';

/** CRM no novo desenho (lote 1): peças comuns das páginas Empresas e Contatos do clone do RD. */

export const BASES_LEGAIS = ['nao_sabe', 'consentimento', 'legitimo_interesse', 'execucao_contrato', 'obrigacao_legal'] as const;
export type BaseLegal = (typeof BASES_LEGAIS)[number];
export { EXTRAS_FORA_DO_CRM };

/** Lote crm6 (item 14): tratamento do contato (vazio = neutro). */
export const TRATAMENTOS = ['sr', 'sra', 'neutro'] as const;
export type Tratamento = (typeof TRATAMENTOS)[number];
/** Lista de 02/11 (item 1): o sexo do contato (no lugar do tratamento na tela; o servidor grava o tratamento junto). */
export const SEXOS = ['masculino', 'feminino', 'nao_informado'] as const;
export type Sexo = (typeof SEXOS)[number];

/** Até quantos registros uma ação em massa leva de uma vez (o mesmo teto do servidor). */
export const LIMITE_EM_MASSA = 500;

export interface ResumoComercial {
  total: number;
  abertas: number;
  ganhas: number;
  perdidas: number;
  ultima_venda: string | null;
  ultimo_contato: string | null;
}

export interface ParceiroCrm extends Parceiro {
  responsavel_id: string | null;
  responsavel_nome: string | null;
  site: string | null;
  base_legal: BaseLegal | null;
  /** Lote crm6 (item 14): tratamento do contato nos modelos de e-mail. */
  tratamento?: Tratamento | null;
  /** Lista de 02/11 (item 1): sexo do contato e tipo do telefone principal (vazio = sem tipo; a tela mostra Comercial). */
  sexo?: Sexo | null;
  telefone_tipo?: TipoMeio | null;
  seguidores: { id: string; nome: string }[];
  /** Com o Pipeline e `pipeline.ver`: negociações, vendas e último contato; null sem negociação. */
  comercial?: ResumoComercial | null;
  /** Ajustes de 27/10 (C5, C13): telefones e e-mails adicionais; segmentos e responsáveis além do principal. */
  meios?: { id: string; meio: 'telefone' | 'email'; tipo: TipoMeio; valor: string }[];
  setores_adicionais?: { id: string; nome: string }[];
  responsaveis_adicionais?: { id: string; nome: string }[];
  /** Lista de 02/11 (item 2a): as empresas além da principal (`parent_id`); a principal arquivada não conta como vínculo. */
  empresas_adicionais?: { id: string; nome: string }[];
  parent_arquivada?: boolean;
  /** Lista de 30/10 (B), item 3: "Todos" no responsável (empresa sem carteira separada). */
  responsavel_todos?: boolean;
}

/**
 * Lista de 30/10 (B), item 3: a opção "Todos" na lista de responsáveis da empresa. Não é usuário: vira `responsavel_todos` nos
 * adicionais (`PUT /cadastros/parceiros/:id/adicionais`); o principal é o primeiro escolhido que é pessoa.
 */
export const RESPONSAVEL_TODOS = '__todos__';

/** Os responsáveis do registro na ordem da lista: "Todos" (se marcado), o principal e os adicionais. */
export function responsaveisDoRegistro(p: Pick<ParceiroCrm, 'responsavel_id' | 'responsaveis_adicionais' | 'responsavel_todos'>): string[] {
  return [...(p.responsavel_todos ? [RESPONSAVEL_TODOS] : []), ...(p.responsavel_id ? [p.responsavel_id] : []), ...(p.responsaveis_adicionais ?? []).map((x) => x.id)];
}

/**
 * O texto dos responsáveis na ficha (empresa e contato): "Todos" (rótulo dado, quando há), o principal e os adicionais, cada nome
 * pelo formatador do modo discreto (`nomePessoa`).
 */
export function textoDosResponsaveis(p: Pick<ParceiroCrm, 'responsavel_nome' | 'responsaveis_adicionais' | 'responsavel_todos'>, rotuloTodos: string): string {
  const nomes = [p.responsavel_nome, ...(p.responsaveis_adicionais ?? []).map((r) => r.nome)].filter((n): n is string => !!n).map((n) => nomePessoa(n));
  return [...(p.responsavel_todos ? [rotuloTodos] : []), ...nomes].join(', ');
}

/** A lista escolhida no formato de gravação: o principal (no cadastro) e os adicionais com o "Todos". */
export function corpoDosResponsaveis(lista: string[]) {
  const pessoas = lista.filter((x) => x !== RESPONSAVEL_TODOS);
  return { principal: { responsavel_id: pessoas[0] ?? null }, adicionais: { responsavel_ids: pessoas.slice(1), responsavel_todos: lista.includes(RESPONSAVEL_TODOS) } };
}

/** Os tipos de telefone da origem (Comercial, Celular, Residencial, Fax, Outro) e WhatsApp (lista de 02/11); "pessoal" vale para e-mail. */
export const TIPOS_TELEFONE = ['comercial', 'celular', 'residencial', 'fax', 'outro', 'whatsapp'] as const;
export type TipoMeio = (typeof TIPOS_TELEFONE)[number] | 'pessoal';

/** Ficha da negociação em página no CRM da casca nova (o mesmo caminho de `pipeline/crm-caminhos.ts`; tela não importa outro módulo). */
export const caminhoDaNegociacaoCrm = (id: string) => `/crm/comercial/negociacao/${id}`;
/** Reunião no CRM da casca nova (o mesmo caminho de `pipeline/crm-caminhos.ts`). */
export const caminhoDoAgendamento = (id: string) => `/crm/reunioes?agendamento=${id}`;

export const CAMINHO_EMPRESAS = '/crm/empresas';
export const CAMINHO_CONTATOS = '/crm/contatos';

export function useMembros() {
  return useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
}

export function useSetores() {
  return useQuery({ queryKey: ['cadastros', 'setores'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/setores') });
}

/** Pode criar negociação daqui (o Comercial instalado e a permissão). */
export function usePodeNegociar() {
  const s = useSessao();
  return s.instalado('pipeline') && s.pode('pipeline.criar');
}

/** Cabeçalho da página (a página da casca não tem a seção de abas): título e as ações à direita. */
export function CabecalhoPagina({ titulo, subtitulo, voltar, children }: { titulo: ReactNode; subtitulo?: ReactNode; voltar?: ReactNode; children?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-center gap-3">
      {voltar}
      {/* Título com largura mínima: no celular os botões descem para a linha de baixo em vez de espremer o título. */}
      <div className="min-w-48 flex-1">
        {subtitulo && <p className="kicker">{subtitulo}</p>}
        <h1 className="text-2xl font-bold tracking-tight break-words">{titulo}</h1>
      </div>
      {children && <div className="flex max-w-full flex-wrap items-center gap-2">{children}</div>}
    </header>
  );
}

/**
 * Barra das ações em massa, como no RD: quantos estão marcados, "Limpar seleção", as ações e "Selecionar todos deste filtro".
 */
export function BarraSelecao({ n, aoLimpar, todos, children }: { n: number; aoLimpar: () => void; todos?: { rotulo: string; aoMarcar: () => void; carregando?: boolean }; children: ReactNode }) {
  const { t } = useTranslation('cadastros');
  if (!n) return null;
  return (
    <div role="region" aria-label={t('crm.selecionados', { count: n })} data-barra-selecao="" className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-acento-forte bg-acento-suave px-3 py-2 text-sm">
      <span className="font-semibold text-tinta-1">{t('crm.selecionados', { count: n })}</span>
      <Botao tamanho="sm" variante="fantasma" onClick={aoLimpar}>
        {t('crm.limparSelecao')}
      </Botao>
      {todos && (
        <Botao tamanho="sm" variante="fantasma" carregando={todos.carregando} onClick={todos.aoMarcar}>
          {todos.rotulo}
        </Botao>
      )}
      <span className="flex-1" />
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

/**
 * Confirmação pela contagem ("Quantas empresas serão transferidas?"): o botão só vale com o número certo digitado. O servidor
 * confere de novo (`confirmacao`).
 */
export function ConfirmarContagem({
  aberto,
  aoFechar,
  titulo,
  texto,
  n,
  botao,
  perigoso,
  podeConfirmar = true,
  aoConfirmar,
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  texto: ReactNode;
  n: number;
  botao: string;
  perigoso?: boolean;
  podeConfirmar?: boolean;
  aoConfirmar: (confirmacao: number) => Promise<void>;
  children?: ReactNode;
}) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const id = useId();
  const [digitado, setDigitado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const certo = Number(digitado) === n && digitado.trim() !== '';
  const fechar = () => {
    setDigitado('');
    aoFechar();
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao
            variante={perigoso ? 'perigo' : 'primario'}
            data-confirmar-contagem=""
            carregando={ocupado}
            disabled={!certo || !podeConfirmar}
            onClick={async () => {
              setOcupado(true);
              try {
                await aoConfirmar(Number(digitado));
                setDigitado('');
              } finally {
                setOcupado(false);
              }
            }}
          >
            {botao}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm">
        <p className="text-tinta-2">{texto}</p>
        {children}
        <Rotulo id={id} rotulo={t('crm.massa.confirmarPergunta')} erro={digitado && !certo ? t('crm.massa.contagemErrada', { n }) : undefined}>
          <Entrada id={id} inputMode="numeric" autoComplete="off" value={digitado} onChange={(e) => setDigitado(e.target.value.replace(/\D/g, ''))} data-contagem="" />
        </Rotulo>
      </div>
    </Modal>
  );
}

/**
 * Ação em massa no servidor; devolve o texto do aviso (alterados e, se houver, a primeira falha). Rodada crm1 (ROB-M1): o
 * servidor aceita até 50 por chamada; a contagem digitada confere o total aqui e cada bloco leva a própria contagem.
 * RCRM5-ROB-M2: devolve também os ids feitos (para só eles saírem da seleção), o total e, se um bloco caiu, `parcial`.
 */
export async function emMassa(ids: string[], acao: 'transferir' | 'setor' | 'seguidor' | 'base_legal' | 'arquivar' | 'desarquivar', valor: string | null, confirmacao: number) {
  type Resposta = { alterados: number; falhas: { id: string; codigo: string; mensagem: string }[] };
  type Total = Resposta & { feitos: string[]; total: number; parcial?: string };
  const unicos = [...new Set(ids)];
  const total: Total = { alterados: 0, falhas: [], feitos: [], total: unicos.length };
  const somar = (bloco: string[], r: Resposta) => {
    total.alterados += r.alterados;
    total.falhas.push(...r.falhas);
    const falhou = new Set(r.falhas.map((f) => f.id));
    total.feitos.push(...bloco.filter((id) => !falhou.has(id)));
  };
  if (confirmacao !== unicos.length) {
    const bloco = unicos.slice(0, TETO_ACAO_EM_MASSA);
    somar(bloco, await post<Resposta>('/cadastros/parceiros/em-massa', { ids: bloco, acao, valor, confirmacao }));
    return total;
  }
  try {
    await porBlocos(unicos, async (bloco) => somar(bloco, await post<Resposta>('/cadastros/parceiros/em-massa', { ids: bloco, acao, valor, confirmacao: bloco.length })));
  } catch (e) {
    // Rodada crm2 (ROB-M1): blocos já gravados contam no aviso (que recarrega a lista); o que faltou vira uma falha só.
    if (!(e instanceof FalhaNoBloco)) throw e;
    total.parcial = mensagemDeErro(e.causa);
    total.falhas.push({ id: '', codigo: 'em_massa.parcial', mensagem: total.parcial });
  }
  return total;
}

export function useAvisoEmMassa() {
  const { t } = useTranslation('cadastros');
  const toast = useToast();
  const qc = useQueryClient();
  return async (r: { alterados: number; falhas: { mensagem: string }[] }) => {
    await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
    await qc.invalidateQueries({ queryKey: ['crm'] });
    if (r.alterados) toast.sucesso(t('crm.massa.feito', { count: r.alterados }));
    if (r.falhas.length) toast.erro(t('crm.massa.falhas', { count: r.falhas.length, motivo: r.falhas[0]!.mensagem }));
  };
}

/** Os ids de todos os registros do filtro (até o teto da ação em massa), para "Selecionar todos deste filtro". */
export async function idsDoFiltro(params: Record<string, unknown>): Promise<string[]> {
  const r = await getTodos<{ id: string }>('/cadastros/parceiros', params, Math.ceil(LIMITE_EM_MASSA / 200) + 1);
  return r.itens.map((x) => x.id).slice(0, LIMITE_EM_MASSA);
}

/** Os registros marcados (de qualquer página), em lotes de 200 ids. */
export async function registrosPorIds<T>(ids: string[]): Promise<T[]> {
  const saida: T[] = [];
  for (let i = 0; i < ids.length; i += 200) {
    saida.push(...(await getTodos<T>('/cadastros/parceiros', { ids: ids.slice(i, i + 200).join(',') })).itens);
  }
  return saida;
}

/** Baixa um CSV (BOM UTF-8, ponto e vírgula), como o modelo de importação. */
export function baixarCsv(nome: string, linhas: (string | number | null | undefined)[][]) {
  const texto = `\uFEFF${linhas.map((l) => l.map(celulaCsv).join(';')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "Sobre a empresa" / "Sobre o contato": a gaveta curta com os dados gerais e o botão de abrir a ficha. */
export function GavetaSobre({ aberto, aoFechar, titulo, linhas, abrir }: { aberto: boolean; aoFechar: () => void; titulo: string; linhas: [string, ReactNode][]; abrir: { rotulo: string; para: string } }) {
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <Link to={abrir.para} onClick={aoFechar} className="inline-flex h-11 items-center rounded-lg border border-borda bg-sup-2 px-4 text-sm font-medium text-tinta-1 hover:bg-sup-3 md:h-10">
          {abrir.rotulo}
        </Link>
      }
    >
      <dl className="flex flex-col gap-4">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="text-xs text-tinta-3">{rotulo}</dt>
            <dd className="mt-0.5 font-semibold break-words text-tinta-1">{valor}</dd>
          </div>
        ))}
      </dl>
    </Drawer>
  );
}

/**
 * "Criar negociação" para uma ou várias empresas ou contatos (a ação em massa do RD): uma negociação por registro, no funil
 * padrão do Comercial, com o nome e o responsável escolhidos.
 */
export function CriarNegociacao({ aberto, aoFechar, parceiros, aoCriar }: { aberto: boolean; aoFechar: () => void; parceiros: { id: string; nome: string }[]; aoCriar?: () => void }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const membros = useMembros();
  const idNome = useId();
  const idResp = useId();
  const [nome, setNome] = useState('');
  const [responsavel, setResponsavel] = useState(s.usuario.id);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  // rcrm4 ROB-M3: id estável por registro (repetir reusa o id; o 409 do mesmo id conta como feito) e o criado sai da lista.
  const idsEstaveis = useRef(new Map<string, string>());
  const [feitos, setFeitos] = useState<ReadonlySet<string>>(new Set());
  const pendentes = parceiros.filter((p) => !feitos.has(p.id));
  const fechar = () => {
    setNome('');
    setErro('');
    setFeitos(new Set());
    idsEstaveis.current.clear();
    aoFechar();
  };
  const criar = async () => {
    if (!nome.trim()) return setErro(tn('erros.obrigatorio'));
    setOcupado(true);
    const novosFeitos = new Set(feitos);
    try {
      for (const p of pendentes) {
        let id = idsEstaveis.current.get(p.id);
        if (!id) idsEstaveis.current.set(p.id, (id = crypto.randomUUID()));
        try {
          await post('/pipeline/oportunidades', { id, titulo: parceiros.length > 1 ? `${nome.trim()} - ${p.nome}`.slice(0, 200) : nome.trim(), parceiro_id: p.id, responsavel_id: responsavel || null, origem: 'manual' });
        } catch (e) {
          if (!(e instanceof ErroRequisicao && e.status === 409 && e.corpo.code === 'registro.duplicado')) throw e;
        }
        novosFeitos.add(p.id);
      }
      toast.sucesso(t('crm.negociacao.criada', { count: novosFeitos.size }));
      fechar();
      aoCriar?.();
    } catch (e) {
      setFeitos(novosFeitos);
      if (novosFeitos.size && parceiros.length > 1) toast.erro(t('crm.negociacao.parcial', { feitos: novosFeitos.size, total: parceiros.length }));
      toast.erro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
      await qc.invalidateQueries({ queryKey: ['crm'] });
    }
  };
  return (
    <Drawer
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('crm.negociacao.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ocupado} onClick={() => void criar()} data-criar-negociacao="">
            {t('crm.negociacao.titulo')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id={idNome} rotulo={t('crm.negociacao.nome')} obrigatorio erro={erro || undefined}>
          <Entrada id={idNome} value={nome} maxLength={200} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id={idResp} rotulo={t('crm.campos.responsavel')}>
          <Selecao id={idResp} value={responsavel} onChange={(e) => setResponsavel(e.target.value)} opcoes={(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))} />
        </Rotulo>
        {parceiros.length > 1 && <p className="text-sm text-tinta-3">{pendentes.map((p) => nomePessoa(p.nome)).join(', ')}</p>}
      </div>
    </Drawer>
  );
}

/**
 * crm4 (L2): "Criar negociação" das ações em massa de Empresas e Contatos. Abre o formulário completo do Pipeline (publicado
 * como criação `negociacao`; Cadastros não importa o Pipeline) no modo lote: nome, responsável, fonte, campanha, funil e
 * etapa, e uma negociação por registro. Sem o formulário do Pipeline, cai no curto de sempre.
 */
export function CriarNegociacaoEmLote({ parceiros, aoFechar, aoCriar }: { parceiros: Pick<ParceiroCrm, 'id' | 'nome' | 'tipo' | 'parent_id' | 'parent_nome'>[]; aoFechar: () => void; aoCriar?: () => void }) {
  const criacao = useCriacaoDaChave('negociacao');
  if (!criacao) return <CriarNegociacao aberto parceiros={parceiros.map((p) => ({ id: p.id, nome: p.nome }))} aoFechar={aoFechar} aoCriar={aoCriar} />;
  // O contato vai com a empresa dele (a negociação é da empresa e o contato entra como principal); a empresa vai sozinha.
  const lote = parceiros.map((p) =>
    p.tipo === 'pessoa' ? { contato: { id: p.id, nome: p.nome }, ...(p.parent_id ? { empresa: { id: p.parent_id, nome: p.parent_nome ?? '' } } : {}) } : { empresa: { id: p.id, nome: p.nome } },
  );
  return (
    <DialogoCriacao
      aberta={criacao}
      aoFechar={aoFechar}
      contexto={{ lote }}
      aoCriar={() => aoCriar?.()}
    />
  );
}

/** Avatar com as iniciais e o nome no title (o responsável na lista de Empresas e nas negociações, como no RD). */
export function Iniciais({ nome }: { nome: string | null | undefined }) {
  if (!nome) return null;
  const visivel = nomePessoa(nome);
  return (
    <span title={visivel} className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ciano/8 text-xs font-semibold text-tinta-1">
      <span aria-hidden>{iniciais(nome).replace(/\./g, '').replace(/\s/g, '').slice(0, 2)}</span>
      <span className="sr-only">{visivel}</span>
    </span>
  );
}

export { FiltrosSalvos } from '../../componentes/filtros-salvos';

/** Período de um filtro de data: os presets do RD e, no "personalizado", as duas datas (`<campo>_preset`, `_desde`, `_ate`). */
export function FiltroPeriodo({ campo, rotulo, f, mudar }: { campo: string; rotulo: string; f: Record<string, string>; mudar: (k: string, v: string) => void }) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const preset = f[`${campo}_preset`] ?? (f[`${campo}_desde`] || f[`${campo}_ate`] ? 'personalizado' : '');
  return (
    <div className="flex flex-col gap-2" data-periodo={campo}>
      <Rotulo id={id} rotulo={rotulo}>
        <Selecao
          id={id}
          value={preset}
          vazio={t('crm.aj.qualquerData')}
          opcoes={PRESETS_DATA.map((p) => ({ valor: p, rotulo: t(`crm.aj.presets.${p}`) }))}
          onChange={(e) => {
            mudar(`${campo}_preset`, e.target.value);
            if (e.target.value !== 'personalizado') {
              mudar(`${campo}_desde`, '');
              mudar(`${campo}_ate`, '');
            }
          }}
        />
      </Rotulo>
      {preset === 'personalizado' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Rotulo id={`${id}-de`} rotulo={t('crm.filtros.de')}>
            <CampoData id={`${id}-de`} type="date" value={f[`${campo}_desde`] ?? ''} onChange={(e) => mudar(`${campo}_desde`, e.target.value)} />
          </Rotulo>
          <Rotulo id={`${id}-ate`} rotulo={t('crm.filtros.ate')}>
            <CampoData id={`${id}-ate`} type="date" value={f[`${campo}_ate`] ?? ''} onChange={(e) => mudar(`${campo}_ate`, e.target.value)} />
          </Rotulo>
        </div>
      )}
    </div>
  );
}

/**
 * Ajustes de 27/10 (C13): escolha de vários (segmentos, responsáveis): os escolhidos como selos com o "x", o primeiro marcado
 * como principal, e um select para acrescentar. `acaoNovo` acrescenta um botão ao lado (cadastrar segmento na hora).
 */
export function SelecaoMultipla({ id, rotulo, valores, opcoes, aoMudar, rotuloPrincipal, acaoNovo, ajuda, limite }: { id: string; rotulo: string; valores: string[]; opcoes: Opcao[]; aoMudar: (v: string[]) => void; rotuloPrincipal: string; acaoNovo?: ReactNode; ajuda?: string; limite?: number }) {
  const { t } = useTranslation('cadastros');
  const nome = (v: string) => opcoes.find((o) => o.valor === v)?.rotulo ?? '';
  const pessoas = valores.filter((v) => v !== RESPONSAVEL_TODOS);
  return (
    <Rotulo id={id} rotulo={rotulo} ajuda={ajuda}>
      {valores.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-escolhidos="">
          {valores.map((v) => (
            <li key={v} className="inline-flex items-center gap-1 rounded-full border border-borda bg-sup-2 py-0.5 pr-0.5 pl-3 text-sm text-tinta-1">
              {nome(v)}
              {/* O "Todos" do responsável (lista de 30/10 B) não é pessoa: o principal é a primeira pessoa da lista. */}
              {v === pessoas[0] && pessoas.length > 1 && <span className="text-xs text-tinta-3">({rotuloPrincipal})</span>}
              <button type="button" aria-label={t('crm.aj.tirar', { nome: nome(v) })} onClick={() => aoMudar(valores.filter((x) => x !== v))} className="flex size-8 items-center justify-center rounded-full text-tinta-3 hover:bg-sup-3">
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        {/* rl0211-2 (SPEC-B3): `limite` esconde o "acrescentar" quando o perfil não grava mais do que isso (ex.: adicionais exigem `cadastros.editar`). */}
        {(limite === undefined || valores.length < limite) && <Selecao id={id} data-enter-nao-salva="" value="" vazio={t('crm.aj.acrescentar')} opcoes={opcoes.filter((o) => !valores.includes(o.valor))} onChange={(e) => e.target.value && aoMudar([...valores, e.target.value])} />}
        {acaoNovo}
      </div>
    </Rotulo>
  );
}

/** Lista de 30/10 (B), item 7.1: a casca da edição rápida mudou para `componentes/edicao-rapida.tsx` (a negociação usa a mesma). */
export { EdicaoRapida };

/**
 * Edição rápida de um campo simples (texto, área, data ou select com "limpar"): Enter salva (fora da área), Esc cancela;
 * obrigatório vazio mostra "Campo obrigatório". Com `prefixo` (o "@" do WhatsApp), o texto fixo aparece no input e na linha.
 */
export function LinhaEditavel({
  rotulo,
  exibir,
  valor,
  tipo = 'texto',
  opcoes,
  obrigatorio,
  pode,
  aoSalvar,
  apos,
  prefixo,
  ...dados
}: {
  rotulo: string;
  exibir: ReactNode;
  /** Ação ao lado da linha, fora do botão (abrir o site): nada interativo dentro do botão da edição. */
  apos?: ReactNode;
  valor: string;
  tipo?: 'texto' | 'area' | 'selecao' | 'url';
  opcoes?: Opcao[];
  obrigatorio?: boolean;
  pode: boolean;
  aoSalvar: (v: string) => Promise<void>;
  /** Texto fixo antes do valor, só visual (não entra no que é salvo). */
  prefixo?: string;
  [dado: `data-${string}`]: string;
}) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const [texto, setTexto] = useState(valor);
  return (
    <EdicaoRapida
      rotulo={rotulo}
      exibir={exibir && prefixo ? <>{prefixo}{exibir}</> : exibir}
      obrigatorio={obrigatorio}
      pode={pode}
      apos={apos}
      enterSalva={tipo !== 'area'}
      aoAbrir={() => setTexto(valor)}
      aoSalvar={async () => {
        if (obrigatorio && !texto.trim()) throw new Error(tn('erros.obrigatorio'));
        await aoSalvar(prefixo && texto.startsWith(prefixo) ? texto.trim().slice(prefixo.length).trim() : texto.trim());
      }}
      {...dados}
    >
      {({ id, erro }) => (
        <Rotulo id={id} rotulo={rotulo} obrigatorio={obrigatorio} erro={erro || undefined}>
          {tipo === 'area' ? (
            <textarea id={id} rows={3} maxLength={5000} value={texto} onChange={(e) => setTexto(e.target.value)} className="rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1" />
          ) : tipo === 'selecao' ? (
            <div className="flex items-center gap-1">
              <Selecao id={id} value={texto} vazio={tn('comum.selecione')} opcoes={opcoes ?? []} onChange={(e) => setTexto(e.target.value)} />
              {texto && !obrigatorio && (
                <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.aj.limpar')} title={t('crm.aj.limpar')} icone={<X className="size-4" />} onClick={() => setTexto('')} />
              )}
            </div>
          ) : prefixo ? (
            <div className="relative">
              <span aria-hidden data-prefixo-campo="" className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-tinta-3">
                {prefixo}
              </span>
              <Entrada id={id} type="text" className="pl-7" maxLength={200} value={texto} onChange={(e) => setTexto(e.target.value.startsWith(prefixo) ? e.target.value.slice(prefixo.length) : e.target.value)} aria-invalid={!!erro} />
            </div>
          ) : (
            <Entrada id={id} type={tipo === 'url' ? 'url' : 'text'} maxLength={tipo === 'url' ? 300 : 200} value={texto} onChange={(e) => setTexto(e.target.value)} aria-invalid={!!erro} />
          )}
        </Rotulo>
      )}
    </EdicaoRapida>
  );
}

/** Edição rápida de uma lista (segmentos, responsáveis): os escolhidos como etiquetas, o primeiro é o principal; seleção múltipla como no formulário. */
export function LinhaMultiplaEditavel({
  rotulo,
  exibir,
  valores,
  opcoes,
  rotuloPrincipal,
  pode,
  aoSalvar,
  ajuda,
  ...dados
}: {
  rotulo: string;
  exibir: ReactNode;
  valores: string[];
  opcoes: Opcao[];
  rotuloPrincipal: string;
  pode: boolean;
  aoSalvar: (v: string[]) => Promise<void>;
  ajuda?: string;
  [dado: `data-${string}`]: string;
}) {
  const [lista, setLista] = useState(valores);
  return (
    <EdicaoRapida rotulo={rotulo} exibir={exibir} pode={pode} aoAbrir={() => setLista(valores)} aoSalvar={() => aoSalvar(lista)} {...dados}>
      {({ id }) => <SelecaoMultipla id={id} rotulo={rotulo} valores={lista} opcoes={opcoes} aoMudar={setLista} rotuloPrincipal={rotuloPrincipal} ajuda={ajuda} />}
    </EdicaoRapida>
  );
}

/**
 * Lista de 30/10 (B), item 3.1: edição rápida de um campo personalizado na ficha (WhatsApp, Porte, Data de fundação, LinkedIn
 * e os outros do workspace), como os campos fixos. O editor é o do formulário (`CampoControlado`, com a lista, a data e o
 * telefone com a máscara do núcleo enquanto digita); a leitura passa pelo formatador do modo discreto (`valorExtra`). Grava o
 * `extras` inteiro com só este campo trocado; vazio grava nulo.
 */
export function ExtraEditavel({
  def,
  extras,
  nomes,
  pode,
  aoSalvar,
  apos,
}: {
  def: DefCampo;
  extras: Record<string, unknown> | null | undefined;
  nomes: (string | null | undefined)[];
  pode: boolean;
  aoSalvar: (extras: Record<string, unknown>) => Promise<void>;
  apos?: ReactNode;
}) {
  const { t: tn } = useTranslation();
  const chave = def.nome.replace(/^extras\./, '');
  const atual = extras?.[chave];
  const [valor, setValor] = useState<unknown>(atual ?? '');
  const vazio = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);
  return (
    <EdicaoRapida
      rotulo={def.rotulo}
      exibir={valorExtra(def, atual, ...nomes)}
      obrigatorio={def.obrigatorio}
      pode={pode}
      apos={apos}
      enterSalva={def.tipo !== 'area'}
      aoAbrir={() => setValor(def.tipo === 'telefone' && typeof atual === 'string' ? mascaraTelefoneDigitando(atual) : (atual ?? ''))}
      aoSalvar={async () => {
        if (def.obrigatorio && vazio(valor)) throw new Error(tn('erros.obrigatorio'));
        const novo = typeof valor === 'string' ? valor.trim() : valor;
        await aoSalvar({ ...(extras ?? {}), [chave]: vazio(novo) ? null : novo });
      }}
      data-campo-rapido={def.nome}
    >
      {() => <CampoControlado def={def} valor={valor} aoMudar={(v) => setValor(def.tipo === 'telefone' && typeof v === 'string' ? mascaraTelefoneDigitando(v) : v)} />}
    </EdicaoRapida>
  );
}

const CAMPOS_ENDERECO_RAPIDO = ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'] as const;
export type DadosEnderecoRapido = Record<(typeof CAMPOS_ENDERECO_RAPIDO)[number], string>;

/**
 * crm4 (L4): edição rápida do endereço na ficha da empresa. Mexe no primeiro endereço (ou cria um se não houver);
 * logradouro e cidade são obrigatórios ("Campo obrigatório"), como na tabela `cadastros_enderecos`.
 */
export function EnderecoEditavel({ rotulo, atual, exibir, pode, aoSalvar, ...dados }: { rotulo: string; atual: Endereco | undefined; exibir: ReactNode; pode: boolean; aoSalvar: (d: DadosEnderecoRapido) => Promise<void>; [dado: `data-${string}`]: string }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const doAtual = (): DadosEnderecoRapido => Object.fromEntries(CAMPOS_ENDERECO_RAPIDO.map((k) => [k, atual?.[k] ?? ''])) as DadosEnderecoRapido;
  const [v, setV] = useState<DadosEnderecoRapido>(doAtual);
  const [faltam, setFaltam] = useState<string[]>([]);
  const s = useSessao();
  // crm5 (E3): o CEP completo consulta o endereço (BrasilAPI pelo servidor) e preenche só os campos vazios, como no cadastro.
  const [consulta, setConsulta] = useState<EstadoConsulta | null>(null);
  const ultimoCep = useRef('');
  const consultarCep = async (cep: string) => {
    const d = cep.replace(/\D/g, '');
    if (d.length !== 8 || d === ultimoCep.current || !s.pode('cadastros.ver')) return;
    ultimoCep.current = d;
    setConsulta('cepBuscando');
    const r = await consultar('cep', d);
    setConsulta(estadoDaResposta(r, 'cep'));
    if (r.situacao !== 'encontrado' || !r.dados) return;
    const novo = enderecoDaConsulta(r.dados);
    setV((x) => {
      const y = { ...x };
      for (const k of CAMPOS_ENDERECO_RAPIDO) if (k !== 'cep' && !y[k].trim() && typeof novo[k] === 'string') y[k] = novo[k] as string;
      return y;
    });
  };
  const rotulos = { cep: t('crm.aj.endereco.cep'), logradouro: t('crm.aj.endereco.logradouro'), numero: t('crm.aj.endereco.numero'), complemento: t('crm.aj.endereco.complemento'), bairro: t('crm.aj.endereco.bairro'), cidade: t('crm.aj.endereco.cidade'), uf: t('crm.aj.endereco.uf') };
  const campo = (k: (typeof CAMPOS_ENDERECO_RAPIDO)[number], id: string, obrigatorio = false) => (
    <Rotulo key={k} id={`${id}-${k}`} rotulo={rotulos[k]} obrigatorio={obrigatorio} erro={faltam.includes(k) ? tn('erros.obrigatorio') : undefined}>
      {k === 'cep' ? (
        <EntradaComMascara
          mascara="oculto"
          id={`${id}-${k}`}
          data-campo-endereco={k}
          inputMode="numeric"
          placeholder={EXEMPLOS_MASCARA.cep}
          formato={mascaraCep}
          valor={v[k]}
          aria-invalid={faltam.includes(k)}
          aoMudarTexto={(valor) => {
            setV((x) => ({ ...x, [k]: valor }));
            void consultarCep(valor);
          }}
        />
      ) : (
        <EntradaSensivel
          mascara="oculto"
          id={`${id}-${k}`}
          data-campo-endereco={k}
          maxLength={k === 'logradouro' ? 200 : k === 'uf' ? 40 : 100}
          value={v[k]}
          aria-invalid={faltam.includes(k)}
          onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))}
        />
      )}
    </Rotulo>
  );
  return (
    <EdicaoRapida
      rotulo={rotulo}
      exibir={exibir}
      pode={pode}
      aoAbrir={() => {
        setV(doAtual());
        setFaltam([]);
        setConsulta(null);
        ultimoCep.current = '';
      }}
      aoSalvar={async () => {
        const sem = (['logradouro', 'cidade'] as const).filter((k) => !v[k].trim());
        setFaltam(sem);
        if (sem.length) throw new Error(tn('erros.obrigatorio'));
        await aoSalvar(Object.fromEntries(CAMPOS_ENDERECO_RAPIDO.map((k) => [k, v[k].trim()])) as DadosEnderecoRapido);
      }}
      {...dados}
    >
      {({ id, erro }) => (
        <div className="flex flex-col gap-3">
          {campo('cep', id)}
          {consulta && (
            <p className="-mt-2 text-xs text-tinta-3" role="status" data-consulta-cep="">
              {t(`consulta.${consulta}`)}
            </p>
          )}
          {campo('logradouro', id, true)}
          <div className="grid grid-cols-2 gap-3">
            {campo('numero', id)}
            {campo('complemento', id)}
          </div>
          {campo('bairro', id)}
          <div className="grid grid-cols-2 gap-3">
            {campo('cidade', id, true)}
            {campo('uf', id)}
          </div>
          {erro && !faltam.length && (
            <p role="alert" className="text-sm text-perigo">
              {erro}
            </p>
          )}
        </div>
      )}
    </EdicaoRapida>
  );
}

/**
 * Ajustes de 27/10 (C3) e lista de 02/11 (itens 2a e 5b): o pop-up central de vincular sem sair da ficha, como no RD: busca, botão de
 * cadastrar (que fecha este e abre o formulário lateral) e rodapé com o primário só depois de escolher. Sem `aoCadastrar`, sem o botão.
 */
export function PopupVincular({
  aberto,
  aoFechar,
  titulo,
  rotulo,
  acao,
  busca,
  cadastrar,
  aoCadastrar,
  marca,
  sucesso,
  aoVincular,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  rotulo: string;
  acao: string;
  busca: { params: Record<string, unknown> };
  cadastrar: string;
  aoCadastrar?: () => void;
  marca: 'empresa' | 'contato' | 'filial';
  sucesso: string;
  aoVincular: (id: string) => Promise<unknown>;
}) {
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const fechar = () => {
    setEscolhido(null);
    aoFechar();
  };
  const vincular = async () => {
    if (!escolhido) return;
    setOcupado(true);
    try {
      await aoVincular(escolhido);
      await invalidarVinculos(qc);
      toast.sucesso(sucesso);
      fechar();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!escolhido} carregando={ocupado} onClick={() => void vincular()} {...{ [`data-vincular-${marca}`]: '' }}>
            {acao}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id={id} rotulo={rotulo}>
          <BuscaRemota id={id} listaNoFluxo def={{ caminho: '/cadastros/parceiros', params: busca.params, rotulo: (i) => String(i.nome), pessoa: true }} valor={escolhido} aoMudar={(v) => setEscolhido(v)} />
        </Rotulo>
        {aoCadastrar && (
          <Botao className="self-start" icone={<Plus className="size-4" />} onClick={() => { setEscolhido(null); aoCadastrar(); }} {...{ [`data-cadastrar-${marca}`]: '' }}>
            {cadastrar}
          </Botao>
        )}
      </div>
    </Modal>
  );
}

/**
 * Rodada rcn-6 (VIS-B6): a releitura da ficha falhou com dado em cache. O bloco de erro fica no topo, fora da vista com a página rolada; o
 * aviso diz o mesmo onde a pessoa está, uma vez por falha.
 */
export function useAvisoDeReleitura(q: { data?: unknown; error: unknown; errorUpdatedAt: number }) {
  const toast = useToast();
  const avisada = useRef(q.errorUpdatedAt);
  useEffect(() => {
    if (q.errorUpdatedAt === avisada.current) return;
    avisada.current = q.errorUpdatedAt;
    if (q.data && q.error) toast.erro(mensagemDeErro(q.error));
  }, [q.errorUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Pop-up do "Vincular empresa" do contato; a busca não oferece as empresas que ele já tem. */
export function VincularEmpresa({ aberto, aoFechar, contatoId, aoCadastrarNova }: { aberto: boolean; aoFechar: () => void; contatoId: string; aoCadastrarNova?: () => void }) {
  const { t } = useTranslation('cadastros');
  return (
    <PopupVincular
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('crm.aj.vincularEmpresa')}
      rotulo={t('crm.campos.empresa')}
      acao={t('crm.aj.vincular')}
      busca={{ params: { papel: 'empresa', sem_vinculo_contato: contatoId } }}
      cadastrar={t('crm.aj.cadastrarEmpresa')}
      aoCadastrar={aoCadastrarNova}
      marca="empresa"
      sucesso={t('crm.aj.empresaVinculada')}
      aoVincular={(empresaId) => vincularContato(contatoId, empresaId)}
    />
  );
}

/** Lista de 02/11 (item 5b): o "+ Adicionar contato" da ficha da empresa; a busca não oferece quem já é contato dela. */
export function AdicionarContatoEmpresa({ aberto, aoFechar, empresaId, aoCadastrarNovo }: { aberto: boolean; aoFechar: () => void; empresaId: string; aoCadastrarNovo?: () => void }) {
  const { t } = useTranslation('cadastros');
  return (
    <PopupVincular
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('acoes.adicionarContato')}
      rotulo={t('crm.campos.contato')}
      acao={t('crm.aj.adicionar')}
      busca={{ params: { papel: 'contato', sem_vinculo_empresa: empresaId } }}
      cadastrar={t('crm.aj.cadastrarContato')}
      aoCadastrar={aoCadastrarNovo}
      marca="contato"
      sucesso={t('crm.aj.contatoAdicionado')}
      aoVincular={(contatoId) => vincularContato(contatoId, empresaId)}
    />
  );
}

/**
 * Lista de 02/11 (item 2a): vincula sem trocar a principal: sem principal, a empresa vira a principal; com principal, vira adicional.
 * `principal` é o pedido explícito de troca (a antiga vira adicional).
 */
export const vincularContato = (contatoId: string, empresaId: string, principal?: boolean) => post(`/cadastros/parceiros/${contatoId}/empresas`, { empresa_id: empresaId, ...(principal ? { principal } : {}) });
export const desvincularContato = (contatoId: string, empresaId: string) => del(`/cadastros/parceiros/${contatoId}/empresas/${empresaId}`);

/** O que muda com um vínculo: as fichas do CRM, as listas e as buscas dos pop-ups (que escondem quem já está vinculado). */
export const invalidarVinculos = (qc: QueryClient) => Promise.all([qc.invalidateQueries({ queryKey: ['crm'] }), qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] }), qc.invalidateQueries({ queryKey: ['busca'] })]);

/** Rodada rcn-5 (ROB-B1): o cadastro saiu do alcance. Com outra falha, a ficha em cache fica na tela, com o erro e "Tentar de novo" em cima. */
export const registroInacessivel = (e: unknown) => e instanceof ErroRequisicao && (e.status === 403 || e.status === 404);

/** A principal (viva e visível para quem lê) e as adicionais do contato, com o nome; é o que as telas mostram como empresa dele. */
export function empresasDoContato(c: Pick<ParceiroCrm, 'parent_id' | 'parent_nome' | 'parent_arquivada' | 'empresas_adicionais'>): { id: string; nome: string; principal: boolean }[] {
  return [
    ...(c.parent_id && c.parent_nome && !c.parent_arquivada ? [{ id: c.parent_id, nome: c.parent_nome, principal: true }] : []),
    ...(c.empresas_adicionais ?? []).map((x) => ({ id: x.id, nome: x.nome, principal: false })),
  ];
}
